// ── Card Fee Calculator ───────────────────────────────────────────────────────
//
// A public lead tool for vending operators, at /card-fees. Nothing is gated —
// the figures update as the visitor types, and no email is asked for before
// the answer. Anyone who would hand over an address before seeing a number is
// not the buyer.
//
// The hook is not the total. It is the SPLIT: on a $1.50 vend the fixed
// per-swipe charge weighs more than the percentage does, so roughly half of a
// vending operator's card bill is driven by transaction COUNT rather than by
// sales. Operators have not seen that decomposed before, and it is the one
// finding on the page they can act on without buying anything.
//
// Deliberately absent: peer benchmarks. Operators want them more than anything
// else here, and we have no peer data — inventing "operators your size average
// 10.4%" would be the fastest way to lose a knowledgeable prospect, and it
// would be a lie. Arithmetic only, with every assumption on screen.
//
// Title and description are NOT set here. scripts/prerender-meta.js writes
// build/card-fees/index.html at build time with this route's tags baked into
// the served HTML, from src/routes.json — which is what makes link previews
// work in the places this gets shared (trade forums, Slack, LinkedIn), since
// unfurlers never execute JavaScript.

import React, { useEffect, useMemo, useState } from "react";
import type {
  CalculatorInputs,
  FeeResult,
  InputField,
  PageProps,
  TargetResult,
} from "./types";

// Defaults describe a plausible mid-size regional operator, so the page shows a
// complete worked answer at rest rather than empty fields. A visitor arriving
// cold from a forum link sees the shape of the result first, then adjusts it.
const DEFAULTS: Readonly<CalculatorInputs> = Object.freeze({
  machines: 500,
  revenuePerMachine: 400,   // $ per machine per month
  cashlessPct: 65,          // % of sales taken on a card
  avgTicket: 1.75,          // $ per card transaction
  ratePct: 5.95,            // processor's percentage rate
  perTxnFee: 0.10,          // $ fixed charge per authorisation
  targetTicket: 2.25,       // $ modelled average ticket
});

// Prices walked in the per-item table. The point it makes is that the effective
// rate falls steeply as the ticket rises, because the fixed charge stops being
// such a large share of a small sale.
const ITEM_PRICES: readonly number[] = Object.freeze([1.00, 1.25, 1.50, 2.00, 2.50, 3.00]);

const INPUT_FIELDS: readonly InputField[] = Object.freeze([
  { key:"machines",          label:"Machines in the fleet",    suffix:"",       min:1,    max:20000, step:1 },
  { key:"revenuePerMachine", label:"Revenue per machine",      suffix:"$ / mo", min:1,    max:5000,  step:10 },
  { key:"cashlessPct",       label:"Sales taken on a card",    suffix:"%",      min:1,    max:100,   step:1 },
  { key:"avgTicket",         label:"Average card transaction", suffix:"$",      min:0.25, max:20,    step:0.05 },
  { key:"ratePct",           label:"Processor rate",           suffix:"%",      min:0,    max:15,    step:0.05 },
  { key:"perTxnFee",         label:"Fee per transaction",      suffix:"$",      min:0,    max:1,     step:0.01 },
] as const);

const money0 = new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", maximumFractionDigits:0 });
const money2 = new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:2, maximumFractionDigits:2 });
const count0 = new Intl.NumberFormat("en-US", { maximumFractionDigits:0 });
const pct1 = (n: number): string => `${n.toFixed(1)}%`;

/** Annual cashless economics for one set of inputs. */
function computeFees(v: CalculatorInputs): FeeResult {
  const annualRevenue = v.machines * v.revenuePerMachine * 12;
  const cashlessRevenue = annualRevenue * (v.cashlessPct / 100);
  const transactions = v.avgTicket > 0 ? cashlessRevenue / v.avgTicket : 0;
  const percentageFees = cashlessRevenue * (v.ratePct / 100);
  const perTransactionFees = transactions * v.perTxnFee;
  const totalFees = percentageFees + perTransactionFees;
  return {
    annualRevenue,
    cashlessRevenue,
    transactions,
    percentageFees,
    perTransactionFees,
    totalFees,
    effectiveRate: cashlessRevenue > 0 ? (totalFees / cashlessRevenue) * 100 : 0,
    countShare: totalFees > 0 ? (perTransactionFees / totalFees) * 100 : 0,
  };
}

/**
 * The same cashless revenue delivered through a larger average ticket, which
 * means fewer authorisations and therefore a smaller fixed-fee bill. The
 * percentage component does not move, because sales have not changed.
 */
function computeAtTarget(v: CalculatorInputs, base: FeeResult): TargetResult {
  const transactions = v.targetTicket > 0 ? base.cashlessRevenue / v.targetTicket : 0;
  const perTransactionFees = transactions * v.perTxnFee;
  const totalFees = base.percentageFees + perTransactionFees;
  return {
    transactions,
    perTransactionFees,
    totalFees,
    saving: base.totalFees - totalFees,
    fewerTransactions: base.transactions - transactions,
    effectiveRate: base.cashlessRevenue > 0 ? (totalFees / base.cashlessRevenue) * 100 : 0,
  };
}

/**
 * An empty field must read as zero rather than NaN, and a pasted value must
 * not be able to drive the arithmetic negative. Takes only `max` so the range
 * slider, which has no InputField of its own, can use it too.
 */
function readNumber(raw: string, bound: { readonly max: number }): number {
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(n, 0), bound.max);
}

export default function CardFeeCalculator({ brand }: PageProps): React.JSX.Element {
  const [v, setV] = useState<CalculatorInputs>({ ...DEFAULTS });

  useEffect(() => {
    // Arriving at /card-fees#talk from a shared link should land on the call
    // to action rather than at the top, but the host serves the same document
    // for every path so the browser has nothing to scroll to until React has
    // rendered.
    if (window.location.hash === "#talk") {
      document.getElementById("talk")?.scrollIntoView();
    }
  }, []);

  const base = useMemo(() => computeFees(v), [v]);
  const target = useMemo(() => computeAtTarget(v, base), [v, base]);
  const saves = target.saving >= 0;

  // Every CalculatorInputs field is a number, which is what lets this take a
  // plain keyof without a generic.
  const set = (key: keyof CalculatorInputs, value: number): void =>
    setV((prev) => ({ ...prev, [key]: value }));

  // Annotated rather than inferred: a bare object literal widens textAlign and
  // textTransform to `string`, which is not assignable to CSSProperties when
  // the const is later spread into a style prop.
  const wrap: React.CSSProperties = { maxWidth:1040, margin:"0 auto", padding:"0 24px", position:"relative" };
  const heading: React.CSSProperties = { fontFamily:"'Bebas Neue',cursive", color:"var(--text-strong)", letterSpacing:"0.02em" };
  const panel: React.CSSProperties = { background:"var(--surface)", border:"1px solid var(--border-subtle)", borderRadius:10, padding:"24px 22px" };
  const label: React.CSSProperties = { fontSize:11, color:"var(--text-faint)", letterSpacing:"0.14em", textTransform:"uppercase", fontWeight:700 };
  const figure: React.CSSProperties = { fontFamily:"'Bebas Neue',cursive", color:"var(--text-strong)", lineHeight:1, letterSpacing:"0.01em" };
  const mono: React.CSSProperties = { fontFamily:"'IBM Plex Mono',monospace", fontVariantNumeric:"tabular-nums" };
  const field: React.CSSProperties = {
    width:"100%", background:"var(--surface-raised)", border:"1px solid var(--border-strong)",
    borderRadius:6, padding:"10px 12px", color:"var(--text-strong)", fontSize:15, outline:"none",
    fontFamily:"'IBM Plex Mono',monospace", fontVariantNumeric:"tabular-nums",
  };
  const th: React.CSSProperties = { ...label, textAlign:"left", padding:"0 10px 8px 0", borderBottom:"1px solid var(--border-strong)" };
  const td: React.CSSProperties = { padding:"9px 10px 9px 0", borderBottom:"1px solid var(--border-subtle)", fontSize:14, color:"var(--text)" };

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", color:"var(--text)" }}>
      <div style={{ position:"fixed", inset:0, backgroundImage:"linear-gradient(var(--surface-raised) 1px,transparent 1px),linear-gradient(90deg,var(--surface-raised) 1px,transparent 1px)", backgroundSize:"44px 44px", opacity:0.4, pointerEvents:"none" }} />

      {/* ── Header ── */}
      <div style={{ ...wrap, display:"flex", justifyContent:"space-between", alignItems:"center", gap:16, flexWrap:"wrap", padding:"20px 24px" }}>
        <a href="/" style={{ display:"inline-flex", alignItems:"baseline", gap:10, textDecoration:"none" }}>
          <span style={{ ...heading, fontSize:30, color:brand.primary }}>{brand.name}</span>
          <span style={{ fontSize:12, color:"var(--text-faint)", letterSpacing:"0.08em", textTransform:"uppercase", fontWeight:700 }}>Vending back office</span>
        </a>
        <a href="#talk" style={{ color:"var(--text-soft)", fontSize:12, fontWeight:700, letterSpacing:"0.08em", textDecoration:"none", textTransform:"uppercase" }}>Get your real number</a>
      </div>

      {/* ── Hero ── */}
      <div style={{ ...wrap, padding:"44px 24px 8px", textAlign:"center" }}>
        <div style={{ fontSize:11, color:brand.primary, letterSpacing:"0.2em", fontWeight:700, textTransform:"uppercase", marginBottom:18 }}>Card fee calculator · no sign-up</div>
        <h1 style={{ ...heading, fontSize:"clamp(34px, 7vw, 56px)", lineHeight:1.05 }}>
          What are card fees<br /><span style={{ color:brand.primary }}>actually costing you?</span>
        </h1>
        <div style={{ fontSize:16, color:"var(--text-muted)", maxWidth:620, margin:"20px auto 0", lineHeight:1.65 }}>
          Change the numbers to match your fleet. Nothing is sent anywhere and nothing is saved — the answer is worked out in your browser as you type.
        </div>
      </div>

      {/* ── Inputs and headline result ── */}
      <div style={{ ...wrap, padding:"40px 24px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20, alignItems:"start" }}>

          <div style={panel}>
            <div style={{ ...label, marginBottom:16 }}>Your fleet</div>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {INPUT_FIELDS.map((f) => (
                <div key={f.key}>
                  <label htmlFor={`cf-${f.key}`} style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8, fontSize:13, color:"var(--text-soft)", marginBottom:5 }}>
                    <span>{f.label}</span>
                    {f.suffix && <span style={{ fontSize:11, color:"var(--text-faint)" }}>{f.suffix}</span>}
                  </label>
                  <input
                    id={`cf-${f.key}`}
                    type="number"
                    inputMode="decimal"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={v[f.key]}
                    onChange={(e) => set(f.key, readNumber(e.target.value, f))}
                    style={field}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize:12, color:"var(--text-faint)", lineHeight:1.6, marginTop:16 }}>
              The last two come from your merchant statement. If you don’t know them, the defaults are typical for vending.
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            <div style={{ ...panel, borderColor:brand.primary }}>
              <div style={{ ...label, marginBottom:10 }}>Card fees, per year</div>
              <div style={{ ...figure, fontSize:"clamp(46px, 11vw, 68px)", color:brand.primary }}>
                {money0.format(base.totalFees)}
              </div>
              <div style={{ fontSize:15, color:"var(--text-muted)", marginTop:10, lineHeight:1.6 }}>
                An effective rate of <strong style={{ color:"var(--text-strong)", ...mono }}>{pct1(base.effectiveRate)}</strong> on {money0.format(base.cashlessRevenue)} of cashless sales, across {count0.format(base.transactions)} transactions.
              </div>
            </div>

            {/* The split is the finding. Lead the eye to it, not to the total. */}
            <div style={panel}>
              <div style={{ ...label, marginBottom:14 }}>Where the bill comes from</div>
              <div
                role="img"
                aria-label={`Percentage-based fees ${pct1(100 - base.countShare)} of the bill, per-transaction fees ${pct1(base.countShare)}`}
                style={{ display:"flex", height:34, borderRadius:5, overflow:"hidden", border:"1px solid var(--border-strong)" }}
              >
                <div style={{ width:`${100 - base.countShare}%`, background:brand.primary }} />
                <div style={{ width:`${base.countShare}%`, background:"var(--warning)" }} />
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:18, marginTop:14 }}>
                <div style={{ flex:"1 1 130px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}>
                    <span style={{ width:10, height:10, borderRadius:2, background:brand.primary, flexShrink:0 }} />
                    <span style={{ fontSize:12, color:"var(--text-soft)", fontWeight:600 }}>Percentage of sales</span>
                  </div>
                  <div style={{ ...mono, fontSize:19, color:"var(--text-strong)", fontWeight:700 }}>{money0.format(base.percentageFees)}</div>
                </div>
                <div style={{ flex:"1 1 130px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}>
                    <span style={{ width:10, height:10, borderRadius:2, background:"var(--warning)", flexShrink:0 }} />
                    <span style={{ fontSize:12, color:"var(--text-soft)", fontWeight:600 }}>Fixed, per transaction</span>
                  </div>
                  <div style={{ ...mono, fontSize:19, color:"var(--text-strong)", fontWeight:700 }}>{money0.format(base.perTransactionFees)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── The hook ── */}
      <div style={{ position:"relative", borderTop:"1px solid var(--border-subtle)", borderBottom:"1px solid var(--border-subtle)", padding:"52px 0" }}>
        <div style={{ ...wrap, textAlign:"center" }}>
          <h2 style={{ ...heading, fontSize:"clamp(26px, 5vw, 38px)", maxWidth:760, margin:"0 auto" }}>
            {pct1(base.countShare)} of your card bill has nothing to do with how much you sell.
          </h2>
          <div style={{ fontSize:15, color:"var(--text-muted)", maxWidth:660, margin:"16px auto 0", lineHeight:1.75 }}>
            It is the fixed charge on every single tap. On a small vend that charge weighs far more heavily than the percentage does — which means the cheapest saving available to you needs no new hardware, no renegotiation and no new supplier. It needs a bigger average ticket.
          </div>
        </div>
      </div>

      {/* ── Target ticket ── */}
      <div style={{ ...wrap, padding:"44px 24px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20, alignItems:"start" }}>
          <div style={panel}>
            <div style={{ ...label, marginBottom:14 }}>The same sales, fewer taps</div>
            <label htmlFor="cf-target" style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8, fontSize:13, color:"var(--text-soft)", marginBottom:8 }}>
              <span>If your average ticket were</span>
              <span style={{ ...mono, fontSize:20, color:brand.primary, fontWeight:700 }}>{money2.format(v.targetTicket)}</span>
            </label>
            <input
              id="cf-target"
              type="range"
              min={0.5}
              max={6}
              step={0.05}
              value={v.targetTicket}
              onChange={(e) => set("targetTicket", readNumber(e.target.value, { max:6 }))}
              style={{ width:"100%", accentColor:brand.primary }}
            />
            <div style={{ fontSize:12, color:"var(--text-faint)", marginTop:8 }}>
              Currently {money2.format(v.avgTicket)}. Raise it through pricing, bundling, or simply making a second selection easy.
            </div>
          </div>

          {/* A smaller target ticket is a cost, and a cost must never read green. */}
          <div style={{ ...panel, background:saves ? "var(--tint-success)" : "var(--tint-danger)", borderColor:saves ? "var(--success)" : "var(--danger)" }}>
            <div style={{ ...label, marginBottom:10 }}>{saves ? "Saved every year" : "Extra cost every year"}</div>
            <div style={{ ...figure, fontSize:"clamp(40px, 9vw, 56px)", color:saves ? "var(--success)" : "var(--danger)" }}>
              {money0.format(Math.abs(target.saving))}
            </div>
            <div style={{ fontSize:14, color:"var(--text-muted)", marginTop:12, lineHeight:1.65 }}>
              {saves
                ? <>The same {money0.format(base.cashlessRevenue)} of cashless sales, delivered through {count0.format(Math.abs(target.fewerTransactions))} fewer transactions. Your effective rate falls from {pct1(base.effectiveRate)} to {pct1(target.effectiveRate)}.</>
                : <>A smaller average ticket means more authorisations for the same sales, so the fixed charge is paid more often. Your effective rate rises from {pct1(base.effectiveRate)} to {pct1(target.effectiveRate)}.</>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Per-item fee table ── */}
      <div style={{ position:"relative", borderTop:"1px solid var(--border-subtle)", padding:"52px 0" }}>
        <div style={wrap}>
          <h2 style={{ ...heading, fontSize:"clamp(24px, 4.5vw, 34px)", textAlign:"center" }}>What it costs to take a card, item by item</h2>
          <div style={{ fontSize:15, color:"var(--text-muted)", maxWidth:640, margin:"14px auto 28px", lineHeight:1.7, textAlign:"center" }}>
            At your rate of {v.ratePct}% plus {money2.format(v.perTxnFee)} per transaction. There are almost certainly items in your machines priced below the point where accepting a card is worth doing.
          </div>
          <div style={{ overflowX:"auto", ...panel, padding:"20px 22px" }}>
            <table style={{ width:"100%", minWidth:360, borderCollapse:"collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Vend price</th>
                  <th style={{ ...th, textAlign:"right" }}>Fee</th>
                  <th style={{ ...th, textAlign:"right" }}>You keep</th>
                  <th style={{ ...th, textAlign:"right" }}>Effective rate</th>
                </tr>
              </thead>
              <tbody>
                {ITEM_PRICES.map((p) => {
                  const fee = p * (v.ratePct / 100) + v.perTxnFee;
                  const rate = (fee / p) * 100;
                  // Compare the DISPLAYED figure, not the raw one: at $1.25 the
                  // true rate is 13.95%, which prints as "14.0%" — flagging on
                  // the raw value leaves a row reading 14.0% un-highlighted
                  // next to one at 16.0% that is, which just looks broken.
                  const steep = Number(rate.toFixed(1)) >= 14;
                  return (
                    <tr key={p}>
                      <td style={{ ...td, ...mono, color:"var(--text-strong)", fontWeight:700 }}>{money2.format(p)}</td>
                      <td style={{ ...td, ...mono, textAlign:"right" }}>{money2.format(fee)}</td>
                      <td style={{ ...td, ...mono, textAlign:"right" }}>{money2.format(p - fee)}</td>
                      <td style={{ ...td, ...mono, textAlign:"right", color:steep ? "var(--danger)" : "var(--text)", fontWeight:steep ? 700 : 400 }}>{pct1(rate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize:13, color:"var(--text-faint)", maxWidth:640, margin:"18px auto 0", lineHeight:1.7, textAlign:"center" }}>
            One thing worth checking on your own statement: if your fees vary transaction to transaction you are probably on <strong style={{color:"var(--text-soft)"}}>interchange-plus</strong> pricing, which is generally the fairer model. A different model called <strong style={{color:"var(--text-soft)"}}>tiered</strong> also produces varying rates and usually costs more. Interchange-plus itemises the networks’ interchange and your processor’s markup separately — if yours shows tiers instead, that is a bigger question than any software decision.
          </div>
        </div>
      </div>

      {/* ── Honesty ── */}
      <div style={{ ...wrap, padding:"44px 24px" }}>
        {/* --surface-raised, not --surface-sunken: in the light palette
            "sunken" is #FFFFFF, identical to the panels around it. */}
        <div style={{ ...panel, background:"var(--surface-raised)" }}>
          <div style={{ ...label, marginBottom:12 }}>What this is and is not</div>
          <div style={{ fontSize:14, color:"var(--text-muted)", lineHeight:1.8 }}>
            Every figure above is arithmetic on the numbers you entered — it is an order of magnitude, not a forecast, and it is only as good as your inputs. We have deliberately not shown you a comparison against “operators your size”, because we do not have that data yet and would rather tell you so than make it up. Your real effective rate is on your settlement statement, and it is the only number that settles the question.
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div id="talk" style={{ position:"relative", borderTop:"1px solid var(--border-subtle)", padding:"56px 0" }}>
        <div style={{ ...wrap, textAlign:"center" }}>
          <h2 style={{ ...heading, fontSize:"clamp(26px, 5vw, 40px)" }}>Want the real number instead of an estimate?</h2>
          <div style={{ fontSize:15, color:"var(--text-muted)", maxWidth:620, margin:"16px auto 28px", lineHeight:1.75 }}>
            Send us one month of itemised processor settlement detail — the version that shows interchange and markup per transaction, not the summary page. We will replace every estimate on this page with your actual figures: your true effective rate, the split, and the prices worth changing. If there is nothing worth chasing, that is a perfectly good answer and you will have it cheaply.
          </div>
          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
            <a
              href={`mailto:${brand.contactEmail}?subject=Card%20fee%20review`}
              style={{ display:"inline-block", background:brand.primary, color:"var(--on-accent)", borderRadius:6, padding:"14px 32px", fontSize:13, fontWeight:700, letterSpacing:"0.1em", textDecoration:"none", textTransform:"uppercase" }}
            >
              {brand.contactEmail}
            </a>
            <a
              href={brand.phoneHref}
              style={{ display:"inline-block", background:"transparent", color:"var(--text-strong)", border:"1px solid var(--border-strong)", borderRadius:6, padding:"14px 32px", fontSize:13, fontWeight:700, letterSpacing:"0.1em", textDecoration:"none", textTransform:"uppercase" }}
            >
              {brand.phone}
            </a>
          </div>
          <div style={{ fontSize:13, color:"var(--text-faint)", marginTop:18 }}>
            No obligation, and no mailing list — {brand.positioning}
          </div>
        </div>
      </div>

      <div style={{ position:"relative", borderTop:"1px solid var(--border-subtle)", padding:"22px 24px", textAlign:"center", fontSize:12, color:"var(--text-faint)" }}>
        {brand.copyright} &copy; {new Date().getFullYear()} &nbsp;·&nbsp;{" "}
        <a href={brand.phoneHref} style={{ color:"var(--text-muted)", textDecoration:"none" }}>{brand.phone}</a> &nbsp;·&nbsp;{" "}
        <a href={`mailto:${brand.contactEmail}`} style={{ color:"var(--text-muted)", textDecoration:"none" }}>{brand.contactEmail}</a> &nbsp;·&nbsp;{" "}
        <a href="/" style={{ color:"var(--text-muted)", textDecoration:"none" }}>What {brand.name} does</a>
      </div>
    </div>
  );
}
